import {
  Answer,
  Answers,
  Question,
  QuestionnaireSchema,
  ValidationErrors,
} from './types'

export class QuestionnaireDataError extends Error {
  constructor(public kind: 'schema' | 'autofill') {
    super(`Invalid questionnaire ${kind}`)
    this.name = 'QuestionnaireDataError'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

// Validate the API boundary; the API remains the only source of field definitions.
export function parseSchema(value: unknown): QuestionnaireSchema {
  const invalid = () => {
    throw new QuestionnaireDataError('schema')
  }
  if (
    !isRecord(value) ||
    value.schemaVersion !== 2 ||
    !Array.isArray(value.questions) ||
    value.questions.length === 0
  )
    return invalid()

  for (const question of value.questions) {
    if (
      !isRecord(question) ||
      typeof question.type !== 'string' ||
      !['single', 'multiple', 'string'].includes(question.type) ||
      typeof question.name !== 'string' ||
      !question.name ||
      typeof question.text !== 'string' ||
      (question.required !== undefined &&
        typeof question.required !== 'boolean') ||
      !Array.isArray(question.choices)
    )
      return invalid()
    for (const choice of question.choices) {
      if (
        !isRecord(choice) ||
        !Number.isInteger(choice.value) ||
        typeof choice.text !== 'string' ||
        (choice.hasTextField !== undefined &&
          typeof choice.hasTextField !== 'boolean') ||
        (choice.exclusive !== undefined &&
          typeof choice.exclusive !== 'boolean')
      )
        return invalid()
    }
    if (
      new Set(question.choices.map((choice) => choice.value)).size !==
      question.choices.length
    )
      return invalid()
    const condition = question.condition
    if (
      condition != null &&
      (!isRecord(condition) ||
        typeof condition.field !== 'string' ||
        !(
          (Number.isInteger(condition.equals) &&
            condition.contains === undefined) ||
          (Number.isInteger(condition.contains) &&
            condition.equals === undefined)
        ))
    )
      return invalid()
  }

  const schema = value as QuestionnaireSchema
  const byName = new Map(
    schema.questions.map((question) => [question.name, question]),
  )
  if (byName.size !== schema.questions.length) return invalid()
  for (const question of schema.questions) {
    const visited = new Set([question.name])
    let condition = question.condition
    while (condition) {
      const parent = byName.get(condition.field)
      const code = condition.equals ?? condition.contains
      if (
        !parent ||
        visited.has(parent.name) ||
        parent.type !==
          (condition.equals !== undefined ? 'single' : 'multiple') ||
        !parent.choices.some((choice) => choice.value === code)
      )
        return invalid()
      visited.add(parent.name)
      condition = parent.condition
    }
  }
  return schema
}

export const emptyAnswer = (question: Question): Answer =>
  question.type === 'multiple' ? [] : question.type === 'string' ? '' : null

export function activeQuestions(
  questions: Question[],
  answers: Record<string, unknown>,
): Question[] {
  const byName = new Map(questions.map((question) => [question.name, question]))
  const active = new Map<string, boolean>()
  const isActive = (question: Question): boolean => {
    if (active.has(question.name)) return active.get(question.name)!
    // Also stops a malformed cyclic dependency from recursing indefinitely.
    active.set(question.name, false)
    const condition = question.condition
    let result = true
    if (condition) {
      const parent = byName.get(condition.field)
      const value = answers[condition.field]
      result =
        !!parent &&
        isActive(parent) &&
        (condition.equals !== undefined
          ? Number.isInteger(value) && value === condition.equals
          : Array.isArray(value) && value.includes(condition.contains))
    }
    active.set(question.name, result)
    return result
  }
  return questions.filter(isActive)
}

export function needsText(question: Question, value: unknown): boolean {
  const selected = Array.isArray(value) ? value : [value]
  return question.choices.some(
    (choice) => choice.hasTextField && selected.includes(choice.value),
  )
}

// Project onto the current schema and clear inactive branches and unused companion text.
export function cleanAnswers(questions: Question[], answers: Answers): Answers {
  const active = new Set(
    activeQuestions(questions, answers).map((question) => question.name),
  )
  return Object.fromEntries(
    questions.flatMap((question) => {
      const value = active.has(question.name)
        ? answers[question.name] ?? emptyAnswer(question)
        : emptyAnswer(question)
      const entries: [string, Answer][] = [[question.name, value]]
      if (active.has(question.name) && needsText(question, value)) {
        entries.push([
          `${question.name}_text`,
          answers[`${question.name}_text`] ?? '',
        ])
      }
      return entries
    }),
  )
}

export function validateAnswers(
  questions: Question[],
  answers: Record<string, unknown>,
  partial = false,
): ValidationErrors {
  const errors: ValidationErrors = {}
  const known = new Set(
    questions.flatMap((question) => [
      question.name,
      ...(question.choices.some((choice) => choice.hasTextField)
        ? [`${question.name}_text`]
        : []),
    ]),
  )
  for (const key of Object.keys(answers)) {
    if (!known.has(key)) errors[key] = 'unknown'
  }
  for (const question of activeQuestions(questions, answers)) {
    const value = answers[question.name] ?? emptyAnswer(question)
    const validCode = (code: unknown) =>
      Number.isInteger(code) &&
      question.choices.some((choice) => choice.value === code)
    const invalid =
      question.type === 'single'
        ? value !== null && !validCode(value)
        : question.type === 'multiple'
          ? !Array.isArray(value) ||
            !value.every(validCode) ||
            new Set(value).size !== value.length
          : typeof value !== 'string'
    if (invalid) {
      errors[question.name] = 'invalid'
      continue
    }
    if (
      Array.isArray(value) &&
      value.length > 1 &&
      question.choices.some(
        (choice) => choice.exclusive && value.includes(choice.value),
      )
    ) {
      errors[question.name] = 'exclusive'
    }
    const empty =
      value === null ||
      (Array.isArray(value)
        ? value.length === 0
        : typeof value === 'string' && !value.trim())
    if (question.required && empty && !partial)
      errors[question.name] = 'required'
    if (needsText(question, value)) {
      const key = `${question.name}_text`
      const text = answers[key] ?? ''
      if (typeof text !== 'string') errors[key] = 'invalid'
      else if (!text.trim() && !partial) errors[key] = 'required'
    }
  }
  return errors
}

export function parseAutofill(
  questions: Question[],
  filledQuestionnaire: string,
): { answers: Answers; explanation: string } {
  try {
    const value: unknown = JSON.parse(filledQuestionnaire)
    if (!isRecord(value)) throw new Error('Expected object')
    const { forklaring, ...answers } = value
    if (forklaring != null && typeof forklaring !== 'string')
      throw new Error('Invalid explanation')
    if (Object.keys(validateAnswers(questions, answers, true)).length)
      throw new Error('Invalid answers')
    return {
      answers: cleanAnswers(questions, answers as Answers),
      explanation: forklaring ?? '',
    }
  } catch {
    throw new QuestionnaireDataError('autofill')
  }
}

// Only active fields and selected companion text are included in the final payload.
export function submissionAnswers(
  questions: Question[],
  answers: Answers,
): Answers {
  const active = activeQuestions(questions, answers)
  const result = cleanAnswers(active, answers)
  return Object.fromEntries(
    Object.entries(result).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ]),
  )
}
